import React, { useEffect, useMemo, useRef, useState } from 'react';
import VideoPane from '../../ui/VideoPane.jsx';

/** Une photo reste affichée quelques secondes après avoir été prise. */
const PHOTO_HOLD_MS = 8000;
/** L'enregistrement couvre l'instant : bornes du registre (exactes) ou, à défaut, heures de réception (marge d'un segment). */
const covers = (source, at) => Number.isFinite(source.startMs) && at >= source.startMs - 1000
    && at <= (source.endMs || source.startMs) + (source.exactRange ? 1000 : 10000);
const clockText = ms => new Date(ms).toLocaleTimeString('fr-FR');

/**
 * Lecteur d'une personne du rendez-vous : ses vidéos et ses sons, suivis sur
 * l'horloge commune (l'enregistrement qui couvre le curseur), et ses photos,
 * affichées à l'instant où elles ont été prises.
 */
export default function RendezVousParticipantPlayer({ track, bundle, clock, t, hlsConfig, fetchBlob }) {
    const view = useMemo(() => ({ ...bundle, tracks: [track] }), [bundle, track]);
    const sources = useMemo(() => track.media || [], [track.media]);
    const [sourceId, setSourceId] = useState(() => (sources.find(s => s.type === 'video') || sources[0])?.id || null);
    const [muted, setMuted] = useState(false);
    const [volume, setVolume] = useState(1);
    const [rotationMode, setRotationMode] = useState('locked');
    const [photoUrls, setPhotoUrls] = useState({});
    const createdUrls = useRef([]);
    const sectionRef = useRef(null);

    const coveringId = useMemo(() => {
        const current = sources.find(s => s.id === sourceId);
        if (current && covers(current, t)) return current.id;
        const matching = sources.filter(s => covers(s, t));
        return (matching.find(s => s.type === 'video') || matching[0])?.id || null;
    }, [sources, sourceId, t]);
    useEffect(() => { if (coveringId && coveringId !== sourceId) setSourceId(coveringId); }, [coveringId, sourceId]);
    const source = sources.find(s => s.id === sourceId) || null;
    const recording = source && covers(source, t);

    // Photo prise à l'instant : affichée par-dessus le lecteur pendant quelques secondes.
    const photo = useMemo(() => {
        const taken = (track.photos || []).filter(p => p.t <= t && t - p.t < PHOTO_HOLD_MS);
        return taken.length ? taken[taken.length - 1] : null;
    }, [track.photos, t]);
    useEffect(() => {
        if (!photo || photo.id in photoUrls) return undefined;
        let cancelled = false;
        fetchBlob(photo.url)
            .then(blob => {
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                createdUrls.current.push(url);
                setPhotoUrls(previous => ({ ...previous, [photo.id]: url }));
            })
            .catch(() => { if (!cancelled) setPhotoUrls(previous => ({ ...previous, [photo.id]: null })); });
        return () => { cancelled = true; };
    }, [photo, photoUrls, fetchBlob]);
    useEffect(() => () => createdUrls.current.forEach(url => URL.revokeObjectURL(url)), []);

    return (
        <section ref={sectionRef} className="sid-video rid-player" style={{ borderColor: track.color }}>
            <header className="rid-player-header">
                <span><i style={{ background: track.color }} />{track.label}</span>
                <span className="rid-player-state">
                    {photo ? (photo.purpose === 'arrival' ? '📸 Photo d’arrivée' : '📸 Photo')
                        : recording ? `${source.type === 'video' ? '🎥' : '🎙️'} ${source.label.split(' — ')[0]}` : 'Pas de capture'}
                </span>
                {sources.length > 0 && (
                    <div className="rid-player-audio">
                        <button
                            type="button"
                            onClick={() => setMuted(value => !value)}
                            title={muted || volume === 0 ? 'Activer le son' : 'Couper le son'}
                        >
                            {muted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
                        </button>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            value={muted ? 0 : volume}
                            onChange={(e) => {
                                const newVol = parseFloat(e.target.value);
                                setVolume(newVol);
                                if (muted && newVol > 0) setMuted(false);
                            }}
                            className="rid-volume-slider"
                            title={`Volume : ${Math.round((muted ? 0 : volume) * 100)}%`}
                            aria-label={`Volume de ${track.label}`}
                        />
                    </div>
                )}
                <button type="button" title="Plein écran" aria-label="Plein écran" onClick={() => {
                    const element = sectionRef.current;
                    if (!element) return;
                    if (document.fullscreenElement === element) document.exitFullscreen?.().catch(() => {});
                    else element.requestFullscreen?.().catch(() => {});
                }}>⛶</button>
            </header>
            <div className="rid-player-stage">
                {sources.length > 0 ? (
                    <VideoPane bundle={view} clock={clock} hlsConfig={hlsConfig} sourceId={sourceId}
                        volume={muted ? 0 : volume} hideControls rotationMode={rotationMode} />
                ) : (
                    <div className="rid-player-empty">{(track.photos || []).length ? 'Photos seulement' : 'Aucune capture'}</div>
                )}
                {photo && (
                    <div className="rid-player-photo">
                        {photoUrls[photo.id] ? <img src={photoUrls[photo.id]} alt={`Prise par ${track.label}`} />
                            : <span>{photoUrls[photo.id] === null ? 'Photo indisponible' : 'Chargement…'}</span>}
                        <small>{clockText(photo.t)}</small>
                    </div>
                )}
            </div>
            {source?.type === 'video' && (
                <button type="button" className="sid-rotation" onClick={() => setRotationMode(mode => (mode === 'locked' ? 'free' : 'locked'))}>
                    {rotationMode === 'locked' ? 'Rotation libre' : 'Portrait/Paysage'}
                </button>
            )}
        </section>
    );
}
